import { canReadProperties, coreQueryKey } from '@/api/core/authorization';
import {
  readPublicMidtermProperty,
  readPublicMidtermSales,
  readRoomMidtermQuote,
} from '@/api/core/native-public-sales';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { HomepageMock, type MockLocale, type MockScreen } from './homepage-mock';
import {
  dateValue,
  PropertyPlacements,
  PublicImageGroups,
  PublicPropertyContent,
  PublicTypeContent,
} from './native-public-sales-section';
import type { PropertyRuntime } from './property-queries';

const DAY_MS = 24 * 60 * 60 * 1000;
// 닫았다 바로 다시 열 때 같은 요청을 되풀이하지 않는다. 저장하면 invalidateHomepagePreview가 낡은 것으로 표시한다.
const PREVIEW_STALE_MS = 60_000;
const defaultPeriod = () => {
  const now = Date.now();
  return { first: dateValue(new Date(now + DAY_MS)), last: dateValue(new Date(now + 30 * DAY_MS)) };
};

/**
 * "홈페이지에 보이는 위치" 안내 옆에서 여는 미리보기.
 * Core 고객 공개 조회(목록·지점 상세·Room 견적)의 결과를 안내문과 같은 다섯 위치로 나눠 보여 준다.
 * 공개 조회가 기간을 요구해서 내일부터 30일을 고정으로 쓴다. 사진·문구·FAQ는 기간과 무관하다.
 */
export function HomepagePreviewDialog({
  runtime,
  generation,
  propertyId,
  typeId,
  roomId,
}: {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  typeId?: string | null;
  roomId?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState<MockScreen>('detail');
  const [mobile, setMobile] = useState(false);
  const [locale, setLocale] = useState<MockLocale>('ko');
  const [period] = useState(defaultPeriod);
  const state = runtime.session.getSnapshot();
  const allowed =
    state.generation === generation &&
    state.status === 'authorized' &&
    state.authorization !== null &&
    !state.deniedDomains.includes('property') &&
    canReadProperties(state.authorization);
  const list = useQuery({
    queryKey: [...coreQueryKey(runtime.apiNamespace, generation, 'public-midterm-list'), period],
    queryFn: ({ signal }) =>
      readPublicMidtermSales(runtime.client, period.first, period.last, signal),
    enabled: allowed && open,
    retry: false,
    staleTime: PREVIEW_STALE_MS,
    meta: { suppressGlobalError: true },
  });
  const listed = list.data?.some((item) => item.property_id === propertyId) === true;
  const detail = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'public-midterm-property', propertyId),
      period,
    ],
    queryFn: ({ signal }) =>
      readPublicMidtermProperty(runtime.client, propertyId, period.first, period.last, signal),
    enabled: allowed && open && listed,
    retry: false,
    staleTime: PREVIEW_STALE_MS,
    meta: { suppressGlobalError: true },
  });
  const property = detail.data?.property;
  const rooms = detail.data?.rooms ?? [];
  const previewRoom =
    rooms.find((room) => room.room_id === roomId) ??
    rooms.find((room) => !typeId || room.room_type_id === typeId) ??
    rooms[0];
  const quote = useQuery({
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        generation,
        'public-midterm-quote',
        previewRoom?.room_id,
      ),
      period,
    ],
    queryFn: () =>
      readRoomMidtermQuote(runtime.client, previewRoom?.room_id ?? '', period.first, period.last),
    enabled: allowed && open && previewRoom !== undefined,
    retry: false,
    staleTime: PREVIEW_STALE_MS,
    meta: { suppressGlobalError: true },
  });
  if (!allowed) return null;
  const types = property?.room_types ?? [];
  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          setOpen(true);
        }}
      >
        홈페이지에서 보이는 모습 미리보기
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>홈페이지에서 보이는 모습</DialogTitle>
            <DialogDescription>
              Core에 저장된 고객 공개 조회 결과를 홈페이지의 노출 위치별로 나눠 보여 줍니다. 현재
              운영 중인 홈페이지 화면과는 다를 수 있습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex max-h-[70vh] min-h-[60vh] flex-col gap-4 overflow-y-auto">
            <div role="status" className="body-sm space-y-1">
              {list.isError ? (
                <p>공개 결과를 확인할 수 없습니다. 현재 공개 상태를 확인한 뒤 다시 열어 보세요.</p>
              ) : list.isPending ? (
                <p>공개 결과를 불러오고 있습니다.</p>
              ) : listed ? (
                detail.isError ? (
                  <p>지점 상세 공개 결과를 확인할 수 없습니다. 서버 설정을 확인하세요.</p>
                ) : detail.isPending ? (
                  <p>지점 상세를 불러오고 있습니다.</p>
                ) : null
              ) : (
                <p>공개 목록에 이 지점이 없어 미리볼 수 없습니다. 판매 준비 상태를 확인하세요.</p>
              )}
            </div>
            {property && (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <Tabs
                    value={screen}
                    onValueChange={(value) => {
                      setScreen(value as MockScreen);
                    }}
                  >
                    <TabsList>
                      <TabsTrigger value="search">검색 결과</TabsTrigger>
                      <TabsTrigger value="detail">지점 상세</TabsTrigger>
                      <TabsTrigger value="plan">호실 선택 · 평면도</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Tabs
                    value={mobile ? 'mobile' : 'desktop'}
                    onValueChange={(value) => {
                      setMobile(value === 'mobile');
                    }}
                  >
                    <TabsList>
                      <TabsTrigger value="desktop">데스크톱</TabsTrigger>
                      <TabsTrigger value="mobile">모바일</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Tabs
                    value={locale}
                    onValueChange={(value) => {
                      setLocale(value as MockLocale);
                    }}
                  >
                    <TabsList>
                      <TabsTrigger value="ko">한국어</TabsTrigger>
                      <TabsTrigger value="en">English</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                {screen === 'plan' && (
                  <p className="body-sm text-muted-foreground" role="status">
                    {previewRoom === undefined
                      ? '공개된 객실이 없어 평면도를 확인할 수 없습니다.'
                      : quote.isError
                        ? '평면도를 확인할 수 없습니다. 입퇴실 시각과 달력 등 서버 준비 상태를 확인하세요.'
                        : quote.isPending
                          ? '평면도를 불러오고 있습니다.'
                          : null}
                  </p>
                )}
                <div className={mobile ? 'mx-auto w-full max-w-[390px]' : 'w-full'}>
                  <HomepageMock
                    property={property}
                    screen={screen}
                    mobile={mobile}
                    locale={locale}
                    floorPlans={quote.data?.floorPlans}
                  />
                </div>
                <details className="rounded-md border p-3">
                  <summary className="cursor-pointer font-medium">
                    공개 조회 원문으로 확인 (언어별 문구·분류·서버 순서)
                  </summary>
                  <div className="mt-3 space-y-4">
                    <PropertyPlacements property={property} />
                    {types.map((type) => (
                      <section key={type.room_type_id} className="space-y-3">
                        <h3 className="font-medium">{type.room_type_name}</h3>
                        <PublicImageGroups groups={type.room_type_images} />
                        <PublicTypeContent presentation={type.presentation} />
                      </section>
                    ))}
                    {quote.data?.floorPlans && <PublicImageGroups groups={quote.data.floorPlans} />}
                    <PublicPropertyContent presentation={property.presentation} />
                  </div>
                </details>
              </>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
